import { platformForBoard, PLATFORM } from './boardModel';

/**
 * Qué módulo hay enchufado en un puerto, a partir de los milivoltios que reporta
 * la placa (GTMD, GTMA, EVMD).
 *
 * Desde ProtocoloEDU 3.1.0 la placa ya no nombra módulos: manda la lectura cruda
 * y el nombre lo decide el host. Esto es el port de CODE/edu/modules.py del repo
 * de trazabilidad, que es de donde salen los rangos -- se calibran allá, con las
 * herramientas de banco, y se traen acá a mano.
 *
 * Un solo catálogo para todas las placas, porque el kit es uno: cada módulo trae
 * su resistencia R1, y esa R1 es la misma en cualquier placa en la que se lo
 * enchufe. Lo que cambia de placa a placa es el circuito que lee esa R1 -- el
 * ESP32 mide por su ADC, el UNO por un ADS1115 con un pull-down en paralelo --, y
 * por eso los milivoltios del mismo módulo no coinciden entre placas. De ahí una
 * columna de rangos por placa en cada fila.
 *
 * Los códigos libres (SENSOR_LIBRE_n) y el duplicado viejo en desuso de
 * modules.py no están: una lectura que cae ahí queda como módulo desconocido,
 * que es lo mismo que pasaría con ellos, sin un nombre que no significa nada.
 */

export const NO_MODULE_MV = 60;

// Las placas con columna propia en el catálogo.
export const READING_PROFILE = {
  EDUPLUG_POWER: 'eduplugPower',
  EDUPLUG_MINI: 'eduplugMini',
};

/**
 * Lo que no depende del módulo sino del circuito de cada placa.
 *
 * Eduplug Power (ESP32): a la lectura se le suma un offset fijo por puerto y la
 * carga que los módulos de los OTROS puertos le ponen al riel de 5V. SAT1 y SAT2
 * (P4, P5) solo aceptan servos.
 *
 * Eduplug Mini (UNO + ADS1115): IDs en P0-P5 con pull-down de 98.6k; P6 es el
 * canal que mide el 5V, y la lectura se normaliza a 5000 mV con ese 5V (GTVL)
 * para que ni la batería ni la carga corran la identificación.
 */
export const PROFILES = {
  [READING_PROFILE.EDUPLUG_POWER]: {
    name: 'Eduplug Power',
    platform: PLATFORM.ESP32,
    portOffsetMv: {
      0: 9, 1: 5, 2: -4, 3: -10, 6: 12, 7: 12,
    },
    loadMv: {
      0: 1, 1: 1, 2: 1, 3: 1, 4: 2, 5: 2,
    },
    servoPorts: [4, 5],
    // Rangos TEÓRICOS en modules.py, todavía sin medir.
    servoRanges: [
      { note: 'con batería', ranges: [[390, 890], [2473, 2973]] },
      { note: 'sin batería', ranges: [[155, 655], [1567, 2067]] },
    ],
  },
  [READING_PROFILE.EDUPLUG_MINI]: {
    name: 'Eduplug Mini',
    platform: PLATFORM.AVR,
    idPorts: [0, 1, 2, 3, 4, 5],
    supplyPort: 6,
    nominalMv: 5000,
    // Fuera de este rango la lectura del 5V no se cree y se asume la nominal.
    supplyValidMv: [4000, 5600],
  },
};

/**
 * La placa de referencia de cada plataforma: la que se usa con una placa que no
 * tiene columna propia.
 *
 * No es una elección arbitraria. El firmware lee los módulos igual en toda la
 * plataforma -- en cualquier AVR busca el ADS1115 de la Mini, en cualquier ESP32
 * mira los pines del Power --, así que una placa sin calibrar que lee algo lo lee
 * con el circuito de su placa de referencia. Una que no tiene ese hardware lee
 * todo en 0 y queda sin módulos, que es lo correcto.
 */
export const DEFAULT_PROFILE = {
  [PLATFORM.ESP32]: READING_PROFILE.EDUPLUG_POWER,
  [PLATFORM.AVR]: READING_PROFILE.EDUPLUG_MINI,
};

const P = READING_PROFILE.EDUPLUG_POWER;
const M = READING_PROFILE.EDUPLUG_MINI;

/**
 * El catálogo: [nombre, R1 en ohm, rango en la Power, rango en la Mini].
 *
 * Power: mV crudos ya corregidos por offset y carga. Mini: mV normalizados a
 * 5000. Los rangos pueden pisarse (Relé, Motor con hélice y Servomotor en la
 * Power): una lectura en el cruce tiene más de un candidato.
 *
 * Cámara en la Power quedó con un rango de un solo mV al ensanchar sus vecinos en
 * la calibración: así como está no se puede detectar. Es un pendiente de
 * modules.py y se copia tal cual para no nombrar distinto que el banco.
 */
const CATALOG = [
  ['Seguidor de linea', 93100, [280, 345], [61, 500]],
  ['Conversor', 75000, [350, 395], [501, 590]],
  ['Tactil', 61900, [430, 462], [591, 690]],
  ['Motor DC 100RPM', 52300, [463, 553], [691, 787]],
  ['Obstaculo', 45300, [556, 585], [788, 887]],
  ['Servo AT', 39200, [595, 688], [888, 990]],
  ['Color', 34800, [689, 715], [991, 1090]],
  ['Joystick', 27400, [820, 870], [1190, 1305]],
  ['Potenciometro', 22100, [977, 1008], [1410, 1510]],
  ['Luz', 18200, [1075, 1152], [1615, 1725]],
  ['Sonido', 15000, [1238, 1294], [1830, 1935]],
  ['Boton', 12400, [1402, 1501], [2050, 2175]],
  ['Temperatura y humedad', 10200, [1537, 1612], [2295, 2405]],
  ['Gas', 9310, [1613, 1684], [2406, 2524]],
  ['Optico', 8450, [1685, 1762], [2525, 2645]],
  ['LEDs RGB', 6980, [1836, 1922], [2765, 2885]],
  ['Zumbador', 5760, [2008, 2091], [3000, 3120]],
  ['Suelo', 4750, [2125, 2216], [3230, 3339]],
  ['Semaforo', 4320, [2225, 2292], [3340, 3446]],
  ['LED blanco', 3920, [2298, 2333], [3447, 3550]],
  ['LED RGB', 3480, [2335, 2418], [3551, 3676]],
  ['Ultrasonido', 3090, [2420, 2470], [3677, 3793]],
  ['Matriz 8x8 LGO', 2740, [2472, 2570], [3794, 3889]],
  ['Matriz 8x8', 2430, [2572, 2635], [3890, 3998]],
  ['Rele', 2100, [2636, 2696], [3999, 4119]],
  ['Motor con helice', 1820, [2645, 2788], [4120, 4219]],
  ['Servomotor MG90S', 1540, [2765, 2815], [4220, 4338]],
  ['Matriz LED 16x8', 1270, [2812, 2918], [4339, 4405]],
  ['Camara', 1020, [2919, 2919], [4406, 4510]],
  ['Pantalla LCD', 787, [2920, 3070], [4511, 4626]],
];

export const MODULES = CATALOG.map(([name, r1, power, mini]) => ({
  name,
  r1,
  ranges: { [P]: power, [M]: mini },
}));

const within = ([low, high], value) => low <= value && value <= high;

const lookup = (profileKey, value) => MODULES
  .filter((module) => module.ranges[profileKey] && within(module.ranges[profileKey], value))
  .map((module) => module.name);

/**
 * Con qué columna se leen los módulos de una placa, y si es la suya o la de
 * referencia de su plataforma. `board` es la placa de la app ({ profile,
 * compilerBoard }).
 */
export const readingProfileFor = (board = {}) => {
  const { profile = '' } = board || {};
  if (PROFILES[profile]) return { key: profile, fallback: false };
  return { key: DEFAULT_PROFILE[platformForBoard(board)], fallback: true };
};

/**
 * La compensación de carga de la Power para `port`: lo que suman los módulos
 * enchufados en los otros puertos. `knownMv` es la última lectura cruda de cada
 * puerto ({ puerto: mV }).
 */
const loadFor = (profile, port, knownMv) => {
  const loads = profile.loadMv || {};
  if (!(port in loads)) return 0;
  return Object.keys(knownMv).reduce((total, key) => {
    const other = Number(key);
    if (other === port || !(other in loads)) return total;
    const offset = (profile.portOffsetMv || {})[other] || 0;
    return (knownMv[key] + offset >= NO_MODULE_MV) ? total + loads[other] : total;
  }, 0);
};

const classify = (names) => {
  if (names.length === 0) return 'unknown';
  return names.length === 1 ? 'module' : 'ambiguous';
};

/**
 * El 5V con el que se normaliza, o null si no se puede creer y hay que asumir el
 * nominal.
 */
const usableSupply = (profile, supplyMv) => {
  const [low, high] = profile.supplyValidMv;
  return (Number.isFinite(supplyMv) && supplyMv >= low && supplyMv <= high) ? supplyMv : null;
};

/**
 * Nombra lo que hay en un puerto.
 *
 * `kind` dice qué se encontró:
 *   empty            puerto vacío.
 *   module           un módulo del catálogo (`name`).
 *   ambiguous        la lectura cae en más de un rango (`names`).
 *   unknown          hay algo enchufado que no está en el catálogo.
 *   servo / no_servo puertos de servo de la Power: hay un servo, o hay otra cosa.
 *   supply_channel   el canal que mide el 5V en la Mini; nunca es un módulo.
 *   not_an_id_port   un puerto que en esta placa no lee IDs.
 *
 * `fallback` en true es una lectura hecha con la columna de referencia de la
 * plataforma porque la placa no tiene la suya: el nombre es probable, no
 * calibrado.
 *
 * Opciones: `knownMv` es la última lectura cruda de cada puerto (la Power la usa
 * para la compensación de carga) y `supplyMv` el último GTVL (la Mini normaliza
 * con él).
 */
export const interpretModule = ({ key, fallback = false }, port, mv, { knownMv = {}, supplyMv = null } = {}) => {
  const profile = PROFILES[key];
  const reading = {
    port,
    mv,
    valueMv: mv,
    kind: 'empty',
    present: false,
    names: [],
    name: null,
    note: '',
    profile: key,
    fallback,
  };
  if (!profile) return reading;

  if (profile.idPorts && !profile.idPorts.includes(port)) {
    reading.kind = port === profile.supplyPort ? 'supply_channel' : 'not_an_id_port';
    return reading;
  }

  let value = mv;
  if (profile.supplyValidMv) {
    const supply = usableSupply(profile, supplyMv);
    if (supply) value = Math.round((mv * profile.nominalMv) / supply);
  }
  if (profile.portOffsetMv) {
    // La carga solo se suma si en este puerto hay algo: un puerto en 0 sigue vacío.
    value += (profile.portOffsetMv[port] || 0) + (mv ? loadFor(profile, port, knownMv) : 0);
  }
  reading.valueMv = value;
  if (value < NO_MODULE_MV) return reading;
  reading.present = true;

  if (profile.servoPorts && profile.servoPorts.includes(port)) {
    const match = profile.servoRanges.find(({ ranges }) => ranges.some((range) => within(range, value)));
    if (match) {
      reading.kind = 'servo';
      reading.names = ['Servo'];
      reading.name = 'Servo';
      reading.note = match.note;
    } else {
      reading.kind = 'no_servo';
    }
    return reading;
  }

  reading.names = lookup(key, value);
  reading.kind = classify(reading.names);
  reading.name = reading.kind === 'module' ? reading.names[0] : null;
  return reading;
};

/**
 * Una línea para la consola: "P1=Zumbador (2050mV)", "P3=Rele/Motor con helice
 * (2660mV)", con la aclaración cuando el nombre sale de la placa de referencia.
 */
export const describeModule = (reading) => {
  const name = reading.names.length > 0 ? reading.names.join('/') : 'desconocido';
  const extra = [];
  if (reading.note) extra.push(reading.note);
  if (reading.fallback && reading.names.length > 0) extra.push(`aprox., tabla ${PROFILES[reading.profile].name}`);
  return `P${reading.port}=${name} (${reading.mv}mV${extra.length > 0 ? `; ${extra.join('; ')}` : ''})`;
};
